import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Description as DocumentIcon,
  Assessment as ReportIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
} from '@mui/icons-material';
import { getAdminContactDetail, updateContactStatus } from '../../services/api';

const statusColors = {
  new: 'info',
  in_progress: 'warning',
  qualified: 'success',
  converted: 'secondary',
  closed: 'default',
};

const statusOptions = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'converted', label: 'Converted' },
  { value: 'closed', label: 'Closed' },
];

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatShortDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const AdminContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const response = await getAdminContactDetail(id);
        setContact(response.data.contact);
        setChatMessages(response.data.chatMessages || []);
        setDocuments(response.data.documents || []);
        setReports(response.data.reports || []);
      } catch (err) {
        setError('Failed to load contact details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchContact();
  }, [id]);

  const handleStatusChange = async (event) => {
    const newStatus = event.target.value;
    setUpdating(true);
    try {
      await updateContactStatus(id, newStatus);
      setContact((prev) => ({ ...prev, status: newStatus }));
    } catch (err) {
      setError('Failed to update status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDownloadDocument = (docId, filename) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3005';
    const token = localStorage.getItem('adminToken');
    window.open(`${API_URL}/api/documents/download/${docId}?token=${token}`, '_blank');
  };

  const handleDownloadReport = (reportId, filename) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3005';
    const token = localStorage.getItem('adminToken');
    window.open(`${API_URL}/api/reports/download/${reportId}?token=${token}`, '_blank');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !contact) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/contacts')}
          sx={{ mb: 2 }}
        >
          Back to Contacts
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/admin/contacts')}
        sx={{ mb: 3 }}
      >
        Back to Contacts
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Contact Info Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Contact Information
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {contact.name}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {contact.email}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Company
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {contact.company}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Role
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {contact.role}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <FormControl fullWidth size="small" disabled={updating}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={contact.status}
                  onChange={handleStatusChange}
                  label="Status"
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body1">
                  {formatDate(contact.createdAt)}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Documents */}
          <Card sx={{ mt: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Documents ({documents.length})
              </Typography>
              {documents.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No documents uploaded
                </Typography>
              ) : (
                <List dense disablePadding>
                  {documents.map((doc) => (
                    <ListItem
                      key={doc.id}
                      disablePadding
                      sx={{ mb: 1 }}
                      secondaryAction={
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                        >
                          Download
                        </Button>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <DocumentIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.originalName || doc.filename}
                        secondary={formatShortDate(doc.createdAt)}
                        primaryTypographyProps={{ noWrap: true, sx: { maxWidth: 150 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Reports */}
          <Card sx={{ mt: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Reports ({reports.length})
              </Typography>
              {reports.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No reports generated
                </Typography>
              ) : (
                <List dense disablePadding>
                  {reports.map((report) => (
                    <ListItem
                      key={report.id}
                      disablePadding
                      sx={{ mb: 1 }}
                      secondaryAction={
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadReport(report.id, report.filename)}
                        >
                          Download
                        </Button>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <ReportIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={report.title || 'Operational Health Report'}
                        secondary={formatShortDate(report.createdAt)}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Chat Transcript */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Chat Transcript
              </Typography>

              {chatMessages.length === 0 ? (
                <Typography color="text.secondary">
                  No chat messages yet
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
                  {chatMessages.map((message, index) => (
                    <Paper
                      key={message.id || index}
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 2,
                        bgcolor: message.role === 'user' ? 'grey.100' : 'primary.50',
                        borderRadius: 2,
                        borderLeft: 4,
                        borderColor: message.role === 'user' ? 'secondary.main' : 'primary.main',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {message.role === 'user' ? (
                          <PersonIcon sx={{ fontSize: 18, mr: 1, color: 'secondary.main' }} />
                        ) : (
                          <BotIcon sx={{ fontSize: 18, mr: 1, color: 'primary.main' }} />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                          {message.role === 'user' ? contact.name : 'Assistant'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {formatDate(message.createdAt)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.content}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminContactDetail;
